"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

// Colorful PageHeader with accent gradient
export function PageHeader({ title, desc, icon: Icon, accent = "#475569", code, breadcrumb, actions }: {
  title: string;
  desc?: string;
  icon: React.ComponentType<{ className?: string }>;
  accent?: string;
  code?: string;
  breadcrumb?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="space-y-1.5">
        {breadcrumb && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span>{breadcrumb}</span>
            <span className="text-foreground font-medium">{title}</span>
          </div>
        )}
        <div className="flex items-center gap-3">
          {Icon && (
            <div
              className="flex size-10 items-center justify-center rounded-lg shadow-sm"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)`, color: "white" }}
            >
              <Icon className="size-5" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{title}</h1>
              {code && (
                <Badge variant="outline" className="text-xs font-mono" style={{ color: accent, borderColor: `${accent}55` }}>
                  {code}
                </Badge>
              )}
            </div>
            {desc && <p className="text-sm text-muted-foreground max-w-2xl">{desc}</p>}
          </div>
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

// Colorful StatCard with accent gradient and delta indicator
export function StatCard({ label, value, delta, deltaLabel, icon: Icon, accent = "#475569", hint, tone }: {
  label: string;
  value: string;
  delta?: number;
  deltaLabel?: string;
  icon?: React.ComponentType<{ className?: string }>;
  accent?: string;
  hint?: string;
  tone?: string;
}) {
  const positive = (delta ?? 0) >= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
      <Card className="relative overflow-hidden border-border/60 hover:shadow-md transition-shadow">
        <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />
        <CardContent className="pt-5">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
              <p className={cn("text-2xl font-semibold tracking-tight", tone)}>{value}</p>
              {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
            </div>
            {Icon && (
              <div
                className="flex size-10 items-center justify-center rounded-lg"
                style={{ background: `${accent}1A`, color: accent }}
              >
                <Icon className="size-5" />
              </div>
            )}
          </div>
          {delta !== undefined && (
            <div className="mt-3 flex items-center gap-2">
              <span className={cn(
                "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium",
                positive ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-rose-500/10 text-rose-600 dark:text-rose-400"
              )}>
                {positive ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
                {Math.abs(delta)}%
              </span>
              {deltaLabel && <span className="text-xs text-muted-foreground">{deltaLabel}</span>}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Colorful Panel with accent border
export function Card2({ title, subtitle, action, children, className, bodyClass, accent }: {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  bodyClass?: string;
  accent?: string;
}) {
  return (
    <div className={cn("rounded-xl border border-border/60 bg-card overflow-hidden", className)}>
      {(title || action) && (
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3" style={accent ? { borderLeft: `3px solid ${accent}` } : undefined}>
          <div>
            {title && <h3 className="text-sm font-semibold">{title}</h3>}
            {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      <div className={cn("p-4", bodyClass)}>{children}</div>
    </div>
  );
}

// Status badge with color mapping
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Approved: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    Received: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    Completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    Paid: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    Reconciled: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    Delivered: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    Accepted: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    Matched: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    Posted: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    Released: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    "Pending Approval": "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    Pending: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    "Pending Match": "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    Draft: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
    "In Transit": "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    Dispatched: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    Delivering: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    Picking: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    Invoiced: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    "Partially Received": "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    Partial: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    Rejected: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    Failed: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    Cancelled: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    Bounced: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    Mismatch: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    Inspection: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    Requested: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    Authorized: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400",
    Inspected: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    "Credit Note Issued": "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    High: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    Medium: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    Low: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
    critical: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    high: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    medium: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    Pass: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  };
  const cls = map[status] || "bg-slate-500/10 text-slate-600 dark:text-slate-400";
  return <span className={cn("inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium", cls)}>{status}</span>;
}
